(function () {
  const modalSelector = '[data-product-shipping-quote-modal]';
  const formSelector = '.product-shipping-quote-form';
  const trackingStorageKey = 'diggerCityShippingQuotePending';
  const scrollStorageKey = 'diggerCityShippingQuoteScroll';
  const pendingContactFormTypeKey = 'diggerCityPendingContactFormType';
  const pendingContactFormIdKey = 'diggerCityPendingContactFormId';
  const trackingExpiryMs = 30 * 60 * 1000;
  const allowedTrackingFields = [
    'event_id',
    'quote_type',
    'product_title',
    'product_handle',
    'product_id',
    'variant_id',
    'sku',
    'price',
    'currency',
    'page_path',
    'product_url',
    'form_id',
    'event_timestamp',
    'timeframe_type',
    'large_truck_access',
  ];
  let activeModal = null;
  let activeTrigger = null;

  function sanitizeText(value, maxLength = 200) {
    return String(value || '')
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength);
  }

  function sanitizeNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  }

  function getCleanPath() {
    return window.location && window.location.pathname ? window.location.pathname : '';
  }

  function getCleanProductUrl(value) {
    const fallbackPath = getCleanPath();

    try {
      const url = new URL(value || fallbackPath, window.location.origin);
      return `${url.origin}${url.pathname}`;
    } catch (error) {
      return fallbackPath;
    }
  }

  function getSafeTimestamp(value) {
    const timestamp = Number(value);
    return Number.isFinite(timestamp) && timestamp > 0 ? timestamp : Date.now();
  }

  function generateEventId() {
    if (window.crypto && typeof window.crypto.getRandomValues === 'function') {
      const values = new Uint32Array(2);
      window.crypto.getRandomValues(values);
      return `sq_${Date.now().toString(36)}_${values[0].toString(36)}${values[1].toString(36)}`;
    }

    return `sq_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  }

  function normalizeChoice(value) {
    const choice = sanitizeText(value, 60).toLowerCase();
    const allowedChoices = {
      yes: 'yes',
      no: 'no',
      unsure: 'unsure',
      'not sure': 'unsure',
      urgent: 'urgent',
      urgent_asap: 'urgent',
      'urgent / asap': 'urgent',
      flexible: 'flexible_backload',
      'flexible / backload': 'flexible_backload',
      'flexible / backload ok': 'flexible_backload',
      'backload is ok': 'flexible_backload',
      flexible_backload_ok: 'flexible_backload',
      specific: 'specific',
      'specific date': 'specific',
      specific_date: 'specific',
    };

    return allowedChoices[choice] || '';
  }

  function sanitizeQuoteType(value) {
    return value === 'international' ? 'international' : 'nz_delivery';
  }

  function sanitizeTrackingPayload(payload) {
    const safePayload = {};

    allowedTrackingFields.forEach((field) => {
      if (!Object.prototype.hasOwnProperty.call(payload || {}, field)) return;

      if (field === 'price') {
        const price = sanitizeNumber(payload[field]);
        if (price !== undefined) safePayload[field] = price;
        return;
      }

      if (field === 'event_timestamp') {
        safePayload[field] = getSafeTimestamp(payload[field]);
        return;
      }

      if (field === 'page_path') {
        safePayload[field] = getCleanPath();
        return;
      }

      if (field === 'product_url') {
        safePayload[field] = getCleanProductUrl(payload[field]);
        return;
      }

      if (field === 'quote_type') {
        safePayload[field] = sanitizeQuoteType(payload[field]);
        return;
      }

      if (field === 'timeframe_type' || field === 'large_truck_access') {
        const choice = normalizeChoice(payload[field]);
        if (choice) safePayload[field] = choice;
        return;
      }

      const textValue = sanitizeText(payload[field], field === 'product_title' ? 200 : 120);
      if (textValue) safePayload[field] = textValue;
    });

    return safePayload;
  }

  function publishDiggerCityEvent(eventName, payload) {
    const safePayload = sanitizeTrackingPayload(payload);
    const analytics = window.Shopify && window.Shopify.analytics;

    try {
      window.dispatchEvent(new CustomEvent(eventName, { detail: safePayload }));
    } catch (error) {
      try {
        if (window.document && typeof window.document.createEvent === 'function') {
          const customEvent = window.document.createEvent('CustomEvent');
          customEvent.initCustomEvent(eventName, false, false, safePayload);
          window.dispatchEvent(customEvent);
        }
      } catch (fallbackError) {
        // Shopify analytics can still receive the sanitized event payload.
      }
    }

    if (!analytics || typeof analytics.publish !== 'function') {
      if (window.DIGGER_CITY_SHIPPING_QUOTE_TRACKING_DEBUG === true && window.console && typeof window.console.log === 'function') {
        window.console.log('Digger City shipping quote event skipped', eventName, safePayload);
      }

      return false;
    }

    try {
      analytics.publish(eventName, safePayload);

      if (window.DIGGER_CITY_SHIPPING_QUOTE_TRACKING_DEBUG === true && window.console && typeof window.console.log === 'function') {
        window.console.log('Digger City shipping quote event', eventName, safePayload);
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  function getStoredPendingPayload() {
    try {
      const storedPayload = window.sessionStorage.getItem(trackingStorageKey);
      if (!storedPayload) return null;

      const pendingPayload = sanitizeTrackingPayload(JSON.parse(storedPayload));
      const pendingTimestamp = Number(pendingPayload.event_timestamp);

      if (!pendingTimestamp || Date.now() - pendingTimestamp > trackingExpiryMs) {
        window.sessionStorage.removeItem(trackingStorageKey);
        return null;
      }

      return pendingPayload;
    } catch (error) {
      return null;
    }
  }

  function removeStoredPendingPayload() {
    try {
      window.sessionStorage.removeItem(trackingStorageKey);
    } catch (error) {
      return;
    }
  }

  function storePendingPayload(payload) {
    const safePayload = sanitizeTrackingPayload(payload);

    try {
      window.sessionStorage.setItem(trackingStorageKey, JSON.stringify(safePayload));
    } catch (error) {
      return;
    }
  }

  function storeSubmitScrollPosition() {
    try {
      window.sessionStorage.setItem(scrollStorageKey, JSON.stringify({
        scrollY: Math.max(0, Math.round(window.scrollY || window.pageYOffset || 0)),
        event_timestamp: Date.now(),
      }));
    } catch (error) {
      return;
    }
  }

  function getStoredSubmitScrollPosition() {
    try {
      const storedPosition = window.sessionStorage.getItem(scrollStorageKey);
      if (!storedPosition) return null;

      const parsedPosition = JSON.parse(storedPosition);
      const scrollY = Number(parsedPosition.scrollY);
      const timestamp = Number(parsedPosition.event_timestamp);

      if (!Number.isFinite(scrollY) || scrollY < 0 || !timestamp || Date.now() - timestamp > trackingExpiryMs) {
        window.sessionStorage.removeItem(scrollStorageKey);
        return null;
      }

      return scrollY;
    } catch (error) {
      return null;
    }
  }

  function removeStoredSubmitScrollPosition() {
    try {
      window.sessionStorage.removeItem(scrollStorageKey);
    } catch (error) {
      return;
    }
  }

  function storePendingContactForm(formType, formId) {
    try {
      window.sessionStorage.setItem(pendingContactFormTypeKey, formType);
      window.sessionStorage.setItem(pendingContactFormIdKey, formId || '');
    } catch (error) {
      return;
    }
  }

  function getPendingContactForm() {
    try {
      return {
        type: window.sessionStorage.getItem(pendingContactFormTypeKey) || '',
        id: window.sessionStorage.getItem(pendingContactFormIdKey) || '',
      };
    } catch (error) {
      return { type: '', id: '' };
    }
  }

  function removePendingContactForm() {
    try {
      window.sessionStorage.removeItem(pendingContactFormTypeKey);
      window.sessionStorage.removeItem(pendingContactFormIdKey);
    } catch (error) {
      return;
    }
  }

  function getCleanContactSuccessUrl() {
    const currentUrl = new URL(window.location.href);
    const removableParams = [
      'contact_posted',
      'posted_successfully',
      'success',
      'contact_success',
    ];

    removableParams.forEach((param) => {
      currentUrl.searchParams.delete(param);
    });

    if (currentUrl.searchParams.get('form_type') === 'contact') {
      currentUrl.searchParams.delete('form_type');
    }

    if (/contact|success|posted|ProductEnquiryForm-|ProductShippingQuoteForm-/i.test(decodeURIComponent(currentUrl.hash.slice(1)))) {
      currentUrl.hash = '';
    }

    return currentUrl;
  }

  function refreshModalFromCleanUrl(modal) {
    if (!modal || !modal.id || typeof window.fetch !== 'function' || typeof window.DOMParser !== 'function') {
      return Promise.resolve(null);
    }

    const cleanUrl = getCleanContactSuccessUrl();

    return window.fetch(cleanUrl.href, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) return null;

        const parsedDocument = new window.DOMParser().parseFromString(html, 'text/html');
        const freshModal = parsedDocument.getElementById(modal.id);
        if (!freshModal) return null;

        modal.replaceWith(freshModal);
        return freshModal;
      })
      .catch(() => null);
  }

  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
  }

  function getSection(modal) {
    return modal.closest('.main-product') || modal.closest('section') || document;
  }

  function getProductData(modal) {
    const section = getSection(modal);
    const productRoot = section.querySelector('[data-js-product]');
    const productJsonScript = productRoot ? productRoot.querySelector('.data-json-product') : null;
    const productJson = productRoot && productRoot.dataset.jsonProduct
      ? productRoot.dataset.jsonProduct
      : productJsonScript && productJsonScript.textContent;

    if (!productJson) return null;

    try {
      return JSON.parse(productJson);
    } catch (error) {
      return null;
    }
  }

  function getCurrentVariant(modal) {
    const section = getSection(modal);
    const productData = getProductData(modal);
    const variantInput = section.querySelector('[data-js-product-variant-input]');

    if (!productData || !Array.isArray(productData.variants) || !variantInput) return null;

    return (
      productData.variants.find((variant) => String(variant.id) === String(variantInput.value)) ||
      productData.variants.find((variant) => String(variant.id) === String(productData.default_variant_id)) ||
      productData.variants[0] ||
      null
    );
  }

  function formatMoney(cents) {
    const moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }

    return (Number(cents) / 100).toFixed(2);
  }

  function getQuoteTypeFromForm(form) {
    return sanitizeQuoteType(form && form.dataset.productShippingQuoteType);
  }

  function getFormForQuoteType(modal, quoteType) {
    return modal.querySelector(`${formSelector}[data-product-shipping-quote-type="${sanitizeQuoteType(quoteType)}"]`);
  }

  function getActiveQuoteType(modal) {
    const activeTab = modal.querySelector('[data-product-shipping-tab][aria-selected="true"]');
    const activePanel = modal.querySelector('[data-product-shipping-panel]:not([hidden])');
    return sanitizeQuoteType(
      (activeTab && activeTab.dataset.productShippingQuoteType) ||
      (activePanel && activePanel.dataset.productShippingQuoteType) ||
      modal.dataset.productShippingSuccessQuoteType ||
      'nz_delivery'
    );
  }

  function ensureEventId(form) {
    const eventIdField = form && form.querySelector('[data-product-shipping-field="event_id"]');
    if (!eventIdField) return '';

    if (!eventIdField.value) {
      eventIdField.value = generateEventId();
    }

    return eventIdField.value;
  }

  function ensureModalEventIds(modal) {
    modal.querySelectorAll(formSelector).forEach((form) => {
      ensureEventId(form);
    });
  }

  function getChoiceValue(form, name) {
    if (!form) return '';

    const field = form.querySelector(`select[name="${name}"], input[name="${name}"]:checked`);
    return field ? field.value : '';
  }

  function getPreferredDateFields(form) {
    return {
      timingField: form ? form.querySelector('[data-shipping-delivery-timing]') : null,
      preferredDateWrap: form ? form.querySelector('[data-shipping-preferred-date-wrap]') : null,
      preferredDateInput: form ? form.querySelector('[data-shipping-preferred-date-input]') : null,
      preferredDateLabel: form ? form.querySelector('[data-shipping-preferred-date-label]') : null,
    };
  }

  function syncPreferredDeliveryDate(modal) {
    if (!modal) return;

    modal.querySelectorAll(formSelector).forEach((form) => {
      const fields = getPreferredDateFields(form);

      if (!fields.timingField || !fields.preferredDateWrap || !fields.preferredDateInput) {
        return;
      }

      const timingValue = fields.timingField.value || (
        fields.timingField.selectedOptions &&
        fields.timingField.selectedOptions[0] &&
        fields.timingField.selectedOptions[0].value
      ) || '';
      const normalizedTimingValue = normalizeChoice(timingValue);
      const showPreferredDate = normalizedTimingValue === 'urgent' || normalizedTimingValue === 'specific';
      const requirePreferredDate = normalizedTimingValue === 'specific';

      fields.preferredDateWrap.hidden = !showPreferredDate;
      fields.preferredDateWrap.setAttribute('aria-hidden', showPreferredDate ? 'false' : 'true');
      fields.preferredDateInput.required = requirePreferredDate;

      if (fields.preferredDateLabel) {
        const baseLabel = fields.preferredDateLabel.dataset.shippingLabelBase || 'Preferred delivery date';
        fields.preferredDateLabel.textContent = requirePreferredDate ? `${baseLabel} *` : baseLabel;
      }

      if (!showPreferredDate) {
        fields.preferredDateInput.value = '';
      }
    });
  }

  function syncPreferredDeliveryDateFromTarget(target) {
    const modal = target && target.closest ? target.closest(modalSelector) : null;
    if (!modal) return;

    syncPreferredDeliveryDate(modal);
  }

  function getPhoneFields(form) {
    return {
      phoneCodeField: form.querySelector('[data-product-shipping-phone-code-hidden]'),
      phoneCodeSelect: form.querySelector('[data-product-shipping-phone-code-select]'),
      phoneCodeCustom: form.querySelector('[data-product-shipping-phone-code-custom]'),
      phoneCodeCustomWrap: form.querySelector('[data-product-shipping-phone-code-custom-wrap]'),
      phoneNumberField: form.querySelector('[data-product-shipping-phone-number]'),
      fullPhoneField: form.querySelector('[data-product-shipping-phone-full]'),
    };
  }

  function sanitizePhoneCode(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 4);
    return digits ? `+${digits}` : '';
  }

  function sanitizePhoneNumber(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 14);
  }

  function getSelectedPhoneCode(fields) {
    if (!fields.phoneCodeSelect) return '+64';

    if (fields.phoneCodeSelect.value === 'other') {
      return sanitizePhoneCode(fields.phoneCodeCustom ? fields.phoneCodeCustom.value : '');
    }

    return sanitizePhoneCode(fields.phoneCodeSelect.value);
  }

  function syncPhoneFields(modal, options = {}) {
    modal.querySelectorAll(formSelector).forEach((form) => {
      const fields = getPhoneFields(form);

      if (!fields.phoneCodeSelect && !fields.phoneCodeCustom && !fields.phoneCodeField && !fields.phoneNumberField && !fields.fullPhoneField) {
        return;
      }

      const isOther = fields.phoneCodeSelect && fields.phoneCodeSelect.value === 'other';
      const phoneCodeValue = getSelectedPhoneCode(fields) || '';

      if (fields.phoneCodeSelect) {
        fields.phoneCodeSelect.required = true;
      }

      if (fields.phoneCodeCustomWrap) {
        fields.phoneCodeCustomWrap.hidden = !isOther;
        fields.phoneCodeCustomWrap.classList.toggle('is-hidden', !isOther);
        fields.phoneCodeCustomWrap.setAttribute('aria-hidden', isOther ? 'false' : 'true');
      }

      if (fields.phoneCodeCustom) {
        if (isOther) {
          fields.phoneCodeCustom.disabled = false;
          fields.phoneCodeCustom.required = true;
          fields.phoneCodeCustom.value = sanitizePhoneCode(fields.phoneCodeCustom.value);
        } else {
          fields.phoneCodeCustom.required = false;
          fields.phoneCodeCustom.disabled = true;
          fields.phoneCodeCustom.value = '';
        }
      }

      if (fields.phoneNumberField) {
        fields.phoneNumberField.value = sanitizePhoneNumber(fields.phoneNumberField.value);
      }

      if (fields.phoneCodeField) {
        fields.phoneCodeField.value = phoneCodeValue;
      }

      const combinedPhoneNumber = fields.phoneNumberField ? fields.phoneNumberField.value : '';
      const fullPhoneNumber = phoneCodeValue && combinedPhoneNumber ? `${phoneCodeValue} ${combinedPhoneNumber}` : '';

      if (fields.fullPhoneField) {
        fields.fullPhoneField.value = fullPhoneNumber;
      }
    });
  }

  function syncDepotDeliveryField(modal) {
    if (!modal) return;

    modal.querySelectorAll(formSelector).forEach((form) => {
      const hiddenField = form.querySelector('[data-shipping-depot-okay-hidden]');
      if (!hiddenField) return;

      const checkboxField = form.querySelector('[data-shipping-depot-okay-checkbox]');
      hiddenField.value = checkboxField && checkboxField.checked ? 'Yes' : 'No';
    });
  }

  function getTrackingPayload(modal, form) {
    const productData = getProductData(modal) || {};
    const variant = getCurrentVariant(modal) || {};
    const price = variant.price !== undefined ? Number(variant.price) / 100 : modal.dataset.productPrice;
    const quoteType = form ? getQuoteTypeFromForm(form) : getActiveQuoteType(modal);

    return sanitizeTrackingPayload({
      event_id: form ? ensureEventId(form) : '',
      quote_type: quoteType,
      product_title: modal.dataset.productTitle,
      product_handle: modal.dataset.productHandle || productData.handle,
      product_id: modal.dataset.productId || productData.id,
      variant_id: variant.id || modal.dataset.variantId,
      sku: variant.sku || modal.dataset.productSku,
      price,
      currency: modal.dataset.productCurrency || (window.theme && window.theme.currency) || window.shopCurrency,
      page_path: getCleanPath(),
      product_url: modal.dataset.productUrl,
      form_id: form ? form.id : '',
      event_timestamp: Date.now(),
      timeframe_type: form ? (getChoiceValue(form, 'contact[Delivery timing]') || getChoiceValue(form, 'contact[Delivery timeframe]') || getChoiceValue(form, 'contact[Timeframe]')) : '',
      large_truck_access: form ? getChoiceValue(form, 'contact[Large truck access]') : '',
    });
  }

  function syncModalFields(modal) {
    const variant = getCurrentVariant(modal);
    if (!variant) return;

    const sku = variant.sku || '';
    const price = variant.price !== undefined ? formatMoney(variant.price) : '';

    modal.querySelectorAll('[data-product-shipping-field="variant_id"]').forEach((field) => {
      field.value = variant.id || '';
    });

    modal.querySelectorAll('[data-product-shipping-field="sku"]').forEach((field) => {
      field.value = sku;
    });

    modal.querySelectorAll('[data-product-shipping-field="price"]').forEach((field) => {
      field.value = price;
    });

    modal.querySelectorAll('input[name="contact[Subject]"]').forEach((field) => {
      const form = field.closest(formSelector);
      const prefix = getQuoteTypeFromForm(form) === 'international'
        ? 'Shipping Quote Request - International Shipping'
        : 'Shipping Quote Request - NZ Delivery';
      field.value = `${prefix} - ${modal.dataset.productTitle || ''}${sku ? ` - ${sku}` : ''}`;
    });

    modal.dataset.variantId = variant.id || '';
    modal.dataset.productSku = sku;
    modal.dataset.productPrice = variant.price !== undefined ? String(Number(variant.price) / 100) : '';
  }

  function getInitialFocus(modal, trigger) {
    if (trigger) {
      return modal.querySelector('[data-product-shipping-close]');
    }

    if (modal.dataset.productShippingState === 'success') {
      return (
        modal.querySelector('[data-product-shipping-focus="success"]') ||
        modal.querySelector('[data-product-shipping-close]')
      );
    }

    if (modal.dataset.productShippingState === 'error') {
      return (
        modal.querySelector('[data-product-shipping-focus="error"]') ||
        modal.querySelector('.product-shipping-quote-modal__title') ||
        modal.querySelector('[data-product-shipping-close]')
      );
    }

    return modal.querySelector('[data-product-shipping-close]');
  }

  function switchTab(modal, quoteType, shouldPublish = true) {
    const safeQuoteType = sanitizeQuoteType(quoteType);

    modal.querySelectorAll('[data-product-shipping-tab]').forEach((tab) => {
      const isActive = sanitizeQuoteType(tab.dataset.productShippingQuoteType) === safeQuoteType;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    modal.querySelectorAll('[data-product-shipping-panel]').forEach((panel) => {
      const isActive = sanitizeQuoteType(panel.dataset.productShippingQuoteType) === safeQuoteType;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    const form = getFormForQuoteType(modal, safeQuoteType);
    if (form) ensureEventId(form);

    if (shouldPublish) {
      publishDiggerCityEvent('digger_city:shipping_quote_tab_selected', getTrackingPayload(modal, form));
    }

    syncPreferredDeliveryDate(modal);
  }

  function openModal(modal, trigger, shouldFocus = true) {
    if (!modal) return;

    if (activeModal && activeModal !== modal) {
      closeModal(activeModal, false);
    }

    activeModal = modal;
    activeTrigger = trigger || null;
    if (activeTrigger) {
      activeTrigger.setAttribute('aria-expanded', 'true');
    }

    syncModalFields(modal);
    ensureModalEventIds(modal);
    syncPreferredDeliveryDate(modal);
    syncPhoneFields(modal, { forceDefaultCountryCode: true });
    syncDepotDeliveryField(modal);
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    document.body.classList.add('overflow-hidden');

    if (trigger) {
      const form = getFormForQuoteType(modal, getActiveQuoteType(modal));
      publishDiggerCityEvent('digger_city:shipping_quote_opened', getTrackingPayload(modal, form));
    }

    if (!shouldFocus) return;

    const dialog = modal.querySelector('.product-shipping-quote-modal__dialog');
    const focusableElements = dialog ? getFocusableElements(dialog) : [];
    const initialFocus = getInitialFocus(modal, trigger) || focusableElements[0] || dialog;

    if (dialog && typeof trapFocus === 'function') {
      trapFocus(dialog, initialFocus);
    } else if (initialFocus) {
      initialFocus.focus();
    }
  }

  function closeModal(modal, restoreFocus = true) {
    if (!modal) return;

    const focusTarget = restoreFocus ? activeTrigger : null;
    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(focusTarget);
    } else if (focusTarget) {
      focusTarget.focus();
    }

    if (activeTrigger) {
      activeTrigger.setAttribute('aria-expanded', 'false');
    }

    if (activeModal === modal) {
      activeModal = null;
      activeTrigger = null;
    }

    if (modal.dataset.productShippingState === 'success') {
      refreshModalFromCleanUrl(modal);
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-product-shipping-quote-trigger]');
    if (trigger) {
      event.preventDefault();
      const modal = document.getElementById(trigger.dataset.productShippingQuoteTarget);
      if (modal && modal.dataset.productShippingState === 'success' && !shouldOpenSuccessModal(modal)) {
        cleanupSuccessUrl(modal);
        refreshModalFromCleanUrl(modal).then((freshModal) => {
          if (freshModal) openModal(freshModal, trigger);
        });
        return;
      }
      openModal(modal, trigger);
      return;
    }

    const tab = event.target.closest('[data-product-shipping-tab]');
    if (tab) {
      const modal = tab.closest(modalSelector);
      if (!modal) return;

      switchTab(modal, tab.dataset.productShippingQuoteType, true);
      return;
    }

    const closeButton = event.target.closest('[data-product-shipping-close]');
    if (closeButton) {
      const modal = closeButton.closest(modalSelector);
      closeModal(modal);
      return;
    }

    if (event.target.matches('[data-product-shipping-overlay]')) {
      const modal = event.target.closest(modalSelector);
      closeModal(modal);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (!activeModal) return;

    if (event.code && event.code.toUpperCase() === 'ESCAPE') {
      event.preventDefault();
      closeModal(activeModal);
      return;
    }

    const activeTab = event.target.closest('[data-product-shipping-tab]');
    if (!activeTab) return;

    const tabs = Array.from(activeModal.querySelectorAll('[data-product-shipping-tab]'));
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex === -1) return;

    if (event.code === 'ArrowRight' || event.code === 'ArrowDown') {
      event.preventDefault();
      const nextTab = tabs[(currentIndex + 1) % tabs.length];
      nextTab.focus();
      switchTab(activeModal, nextTab.dataset.productShippingQuoteType, true);
    }

    if (event.code === 'ArrowLeft' || event.code === 'ArrowUp') {
      event.preventDefault();
      const previousTab = tabs[(currentIndex - 1 + tabs.length) % tabs.length];
      previousTab.focus();
      switchTab(activeModal, previousTab.dataset.productShippingQuoteType, true);
    }
  });

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-js-product-variant-input]')) return;

    const section = event.target.closest('.main-product') || event.target.closest('section');
    if (!section) return;

    section.querySelectorAll(modalSelector).forEach((modal) => {
      syncModalFields(modal);
      syncPreferredDeliveryDate(modal);
      syncPhoneFields(modal, { forceDefaultCountryCode: true });
      syncDepotDeliveryField(modal);
    });
  });

  document.addEventListener('change', (event) => {
    if (!event.target.closest('[data-shipping-delivery-timing]')) return;
    syncPreferredDeliveryDateFromTarget(event.target);
  });

  document.addEventListener('change', (event) => {
    const phoneCodeSelect = event.target.closest('[data-product-shipping-phone-code-select]');
    if (!phoneCodeSelect) return;

    const modal = event.target.closest(modalSelector);
    if (!modal) return;

    syncPhoneFields(modal, { forceDefaultCountryCode: true });

    const form = event.target.closest(formSelector);
    const fields = form ? getPhoneFields(form) : null;
    if (fields && fields.phoneCodeSelect && fields.phoneCodeSelect.value === 'other' && fields.phoneCodeCustom) {
      fields.phoneCodeCustom.focus();
    }
  });

  document.addEventListener('change', (event) => {
    if (!event.target.closest('[data-shipping-depot-okay-checkbox]')) return;

    const modal = event.target.closest(modalSelector);
    if (!modal) return;

    syncDepotDeliveryField(modal);
  });

  document.addEventListener('input', (event) => {
    const phoneCodeSelect = event.target.closest('[data-product-shipping-phone-code-select]');
    const phoneCodeCustom = event.target.closest('[data-product-shipping-phone-code-custom]');
    const phoneNumberField = event.target.closest('[data-product-shipping-phone-number]');

    if (!phoneCodeSelect && !phoneCodeCustom && !phoneNumberField) return;

    const modal = event.target.closest(modalSelector);
    if (!modal) return;

    if (phoneCodeCustom) {
      phoneCodeCustom.value = sanitizePhoneCode(phoneCodeCustom.value);
    }

    if (phoneNumberField) {
      phoneNumberField.value = sanitizePhoneNumber(phoneNumberField.value);
    }

    syncPhoneFields(modal, { forceDefaultCountryCode: true });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches(formSelector)) return;

    const modal = form.closest(modalSelector);
    if (!modal || modal.dataset.productShippingState !== 'normal') return;

    syncModalFields(modal);
    syncPhoneFields(modal, { forceDefaultCountryCode: true });
    syncDepotDeliveryField(modal);
    ensureEventId(form);

    if (!form.checkValidity()) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof form.reportValidity === 'function') {
        form.reportValidity();
      }
      return;
    }

    storePendingPayload(getTrackingPayload(modal, form));
    storePendingContactForm('shipping_quote', form.id || '');
    storeSubmitScrollPosition();
  });

  function shouldRemoveSuccessHash(modal, currentUrl) {
    if (!currentUrl.hash) return false;

    const hashId = decodeURIComponent(currentUrl.hash.slice(1));
    const successFormId = modal.dataset.productShippingSuccessFormId;

    return hashId === successFormId || /^ProductShippingQuoteForm-/.test(hashId);
  }

  function restoreSubmitScrollPosition() {
    const storedScrollY = getStoredSubmitScrollPosition();
    const scrollY = storedScrollY !== null ? storedScrollY : 0;

    window.scrollTo({
      top: scrollY,
      left: window.scrollX || window.pageXOffset || 0,
      behavior: 'auto',
    });

    window.dispatchEvent(new Event('scroll'));
    removeStoredSubmitScrollPosition();
  }

  function cleanupSuccessUrl(modal) {
    if (!modal || modal.dataset.productShippingState !== 'success') return;

    const currentUrl = getCleanContactSuccessUrl();
    let replaced = false;

    if (shouldRemoveSuccessHash(modal, currentUrl)) {
      currentUrl.hash = '';
    }

    if (currentUrl.href !== window.location.href) {
      window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
      replaced = true;
    }

    if (replaced || getStoredSubmitScrollPosition() !== null) {
      window.requestAnimationFrame(() => {
        window.setTimeout(restoreSubmitScrollPosition, 0);
      });
    }
  }

  function publishConfirmedSubmission(modal) {
    if (!modal || modal.dataset.productShippingSuccess !== 'true') return;

    const pendingPayload = getStoredPendingPayload();
    if (!pendingPayload) return;

    if (modal.dataset.productShippingSuccessFormId && pendingPayload.form_id !== modal.dataset.productShippingSuccessFormId) {
      removeStoredPendingPayload();
      return;
    }

    publishDiggerCityEvent('digger_city:shipping_quote_submitted', pendingPayload);
    removeStoredPendingPayload();
  }

  function shouldOpenSuccessModal(modal) {
    if (!modal) return false;

    if (modal.dataset.productShippingState === 'error') {
      return true;
    }

    if (modal.dataset.productShippingState !== 'success') {
      return false;
    }

    const pendingForm = getPendingContactForm();
    if (pendingForm.type !== 'shipping_quote') return false;
    return true;
  }

  document.querySelectorAll(modalSelector).forEach((modal) => {
    syncModalFields(modal);
    ensureModalEventIds(modal);
    syncPreferredDeliveryDate(modal);
    syncPhoneFields(modal, { forceDefaultCountryCode: true });
    syncDepotDeliveryField(modal);
    publishConfirmedSubmission(modal);
  });

  document.querySelectorAll(`${modalSelector}[data-product-shipping-open-on-load="true"]`).forEach((modal) => {
    if (!shouldOpenSuccessModal(modal)) {
      cleanupSuccessUrl(modal);
      refreshModalFromCleanUrl(modal);
      return;
    }

    openModal(modal, null, true);
    cleanupSuccessUrl(modal);
    removePendingContactForm();
  });
})();
